import React, { createContext, useContext, useEffect, useMemo, useState, useRef } from "react";
import { auth, firestore } from "../firebase";

const ADMIN_EMAIL = "ronak@lunarelectric.in";
const SESSION_TIMEOUT_MS = 24 * 60 * 60 * 1000; // 24 hours

const AuthContext = createContext({
  user: null,
  profile: null,
  loading: true,
});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const lastProcessedUidRef = useRef(null);
  const lastProcessedProfileRef = useRef(null);

  useEffect(() => {
    let isMounted = true;

    const unsubscribe = auth.onAuthStateChanged(async (firebaseUser) => {
      if (!isMounted) return;

      if (!firebaseUser) {
        // FIX: Don't immediately clear state if we're in the middle of user creation
        // This prevents redirect loops when auth state changes temporarily
        const justCreatedUser = sessionStorage.getItem('just_created_user');
        if (justCreatedUser) {
          // User was just created, clear the flag and set loading to false
          // The redirect to login will happen naturally
          sessionStorage.removeItem('just_created_user');
        }
        
        // Reset refs when user logs out
        lastProcessedUidRef.current = null;
        lastProcessedProfileRef.current = null;
        
        setUser(null);
        setProfile(null);
        setLoading(false);
        return;
      }

      try {
        const tokenResult = await firebaseUser.getIdTokenResult();
        const authTime = tokenResult.authTime ? new Date(tokenResult.authTime).getTime() : Date.now();
        const now = Date.now();
        const sessionAge = now - authTime;

        if (sessionAge > SESSION_TIMEOUT_MS) {
          await auth.signOut();
          if (isMounted) {
            setUser(null);
            setProfile(null);
            setLoading(false);
          }
          return;
        }

        const ref = firestore.collection("users").doc(firebaseUser.uid);
        const snap = await ref.get();
        let profileData;

        if (!snap.exists) {
          // FIX: Check if this is a newly created user (just created by admin)
          // If so, don't create profile automatically - it should already exist
          const justCreatedUser = sessionStorage.getItem('just_created_user');
          if (justCreatedUser) {
            // This user was just created, profile should exist
            // Wait a moment and try again, or redirect to login
            console.warn("Newly created user detected, but profile not found yet");
            // The profile creation might be in progress, give it a moment
            await new Promise(resolve => setTimeout(resolve, 500));
            const retrySnap = await ref.get();
            if (retrySnap.exists) {
              profileData = retrySnap.data();
            } else {
              // Profile still doesn't exist, create it
              const isAdmin = firebaseUser.email === ADMIN_EMAIL;
              const name = isAdmin ? "Ronak" : firebaseUser.displayName || firebaseUser.email || "User";
              const codePrefix = isAdmin ? "RN" : name.slice(0, 2).toUpperCase();
              // Check if user is a customer (exists in opexCustomers)
              const customerRef = firestore.collection("opexCustomers").doc(firebaseUser.uid);
              const customerSnap = await customerRef.get();
              
              let role = "sales";
              if (isAdmin) {
                role = "admin";
              } else if (customerSnap.exists) {
                role = "customer";
              }
              
              profileData = {
                name,
                email: firebaseUser.email || "",
                role,
                codePrefix,
                createdAt: Date.now(),
                lastLoginAt: Date.now(),
              };
              await ref.set(profileData);
            }
          } else {
            // Normal flow - create profile for new user
            const isAdmin = firebaseUser.email === ADMIN_EMAIL;
            const name = isAdmin ? "Ronak" : firebaseUser.displayName || firebaseUser.email || "User";
            const codePrefix = isAdmin ? "RN" : name.slice(0, 2).toUpperCase();
            
            // Check if user is a customer (exists in opexCustomers)
            const customerRef = firestore.collection("opexCustomers").doc(firebaseUser.uid);
            const customerSnap = await customerRef.get();
            
            let role = "sales";
            if (isAdmin) {
              role = "admin";
            } else if (customerSnap.exists) {
              role = "customer";
            }
            
            profileData = {
              name,
              email: firebaseUser.email || "",
              role,
              codePrefix,
              createdAt: Date.now(),
              lastLoginAt: Date.now(),
            };
            await ref.set(profileData);
          }
        } else {
          profileData = snap.data();
          
          // Don't update role automatically - this can cause infinite loops
          // Sales agents should have "sales" role, customers should have "customer" role
          // The role is set correctly when the account is created, no need to update on login
        }

        if (isMounted) {
          // Prevent infinite loops by checking if we've already processed this user/profile
          const profileKey = `${firebaseUser.uid}-${profileData.role}-${profileData.email}`;
          
          // Only update if this is a different user or profile
          if (lastProcessedUidRef.current !== firebaseUser.uid || 
              lastProcessedProfileRef.current !== profileKey) {
            lastProcessedUidRef.current = firebaseUser.uid;
            lastProcessedProfileRef.current = profileKey;
            
            setUser(firebaseUser);
            setProfile(profileData);
            setLoading(false);
          } else {
            // Already processed, just ensure loading is false
            setLoading(false);
          }
        }
      } catch (error) {
        console.error("Auth error:", error);
        // FIX: Don't immediately clear everything on error - might be temporary
        // Only clear if it's a real auth error, not a Firestore permission error
        if (error.code === 'permission-denied') {
          // Firestore permission error - might be temporary, keep user but clear profile
          console.warn("Firestore permission error in AuthContext, keeping user session");
          if (isMounted) {
            setProfile(null);
            setLoading(false);
          }
        } else {
          // Real auth error, clear everything
          if (isMounted) {
            setUser(null);
            setProfile(null);
            setLoading(false);
          }
        }
      }
    });

    return () => {
      isMounted = false;
      unsubscribe();
    };
  }, []);

  // Session timeout check every 5 minutes
  useEffect(() => {
    if (!user) return;

    const interval = setInterval(async () => {
      try {
        const tokenResult = await user.getIdTokenResult(true);
        const authTime = tokenResult.authTime ? new Date(tokenResult.authTime).getTime() : Date.now();
        const now = Date.now();
        const sessionAge = now - authTime;

        if (sessionAge > SESSION_TIMEOUT_MS) {
          await auth.signOut();
        }
      } catch (error) {
        console.error("Session check error:", error);
      }
    }, 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, [user]);

  const value = useMemo(() => ({ user, profile, loading }), [user, profile, loading]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuthContext() {
  return useContext(AuthContext);
}


